import {
  isModelKey,
  MODEL_CATALOGUE,
  type RecommendationCounts,
  type RecommendationKindFigures,
} from "@esi/shared";
import { ChevronRight, Download } from "lucide-react";
import { Fragment, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, formatPercent } from "@/components/charts";
import { InlineError, Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Freshness, SCROLL_CUE, Section } from "./parts.tsx";
import { type ReportFilters, useRecommendationExport, useRecommendations } from "./queries.ts";

const COUNT_COLUMNS = ["shown", "accepted", "edited", "dismissed", "ignored"] as const;

/**
 * Recommendation acceptance in the quality report (openspec change `recommendation-outcomes`):
 * one row per kind with its counts and rate; a row opens onto its model and prompt version rows
 * and the reasons people gave. Counts only. Tenant admins may export the figures as JSON.
 */
export function RecommendationsSection({ filters }: { filters: ReportFilters }) {
  const { t } = useTranslation();
  const report = useRecommendations(filters);
  const exporting = useRecommendationExport(filters);
  const title = t("reports.recommendations.title");

  return (
    <Section
      title={title}
      aside={
        report.data?.canExport && report.data.kinds.length > 0 ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={exporting.isPending}
            onClick={() => exporting.mutate()}
          >
            <Download aria-hidden="true" strokeWidth={1.75} />
            {exporting.isPending
              ? t("reports.recommendations.exporting")
              : t("reports.recommendations.export")}
          </Button>
        ) : null
      }
    >
      <p className="max-w-prose text-sm text-fg-secondary">{t("reports.recommendations.lead")}</p>
      {exporting.isError ? (
        <InlineError>{t("reports.recommendations.exportFailed")}</InlineError>
      ) : null}
      {report.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : report.isError ? (
        <Notice tone="danger">{report.error.message}</Notice>
      ) : report.data.kinds.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line px-4 py-6 text-sm text-fg-muted">
          {t("reports.recommendations.empty")}
        </p>
      ) : (
        <>
          <Freshness freshness={report.data.freshness} />
          <RecommendationTable caption={title} kinds={report.data.kinds} />
          <p className="max-w-prose text-xs text-fg-muted">{t("reports.recommendations.note")}</p>
        </>
      )}
    </Section>
  );
}

/** The counts of one row, in column order, then the rate ("—" under ten outcomes). */
function CountCells(props: { counts: RecommendationCounts; muted?: boolean }) {
  const { t } = useTranslation();
  const { counts } = props;
  const values = {
    shown: counts.shown,
    accepted: counts.accepted,
    edited: counts.acceptedAfterEdit,
    dismissed: counts.dismissed,
    ignored: counts.ignored,
  };
  const cell = cn(
    "whitespace-nowrap px-3 py-2 text-right tabular-nums",
    props.muted ? "text-fg-muted" : "text-fg-secondary",
  );
  return (
    <>
      {COUNT_COLUMNS.map((column) => (
        <td key={column} className={cell}>
          {formatNumber(values[column])}
        </td>
      ))}
      <td className={cn(cell, !props.muted && "font-medium text-fg")}>
        {counts.acceptanceRate === null ? (
          <>
            <span aria-hidden="true">{t("reports.recommendations.noRate")}</span>
            <span className="sr-only">{t("reports.recommendations.noRateLabel")}</span>
          </>
        ) : (
          formatPercent(counts.acceptanceRate)
        )}
      </td>
      {/* Dismissed over shown (feedback-from-conversation: tuning reads false positives). */}
      <td className={cell}>
        {counts.dismissalRate === null || counts.dismissalRate === undefined ? (
          <>
            <span aria-hidden="true">{t("reports.recommendations.noRate")}</span>
            <span className="sr-only">{t("reports.recommendations.noRateLabel")}</span>
          </>
        ) : (
          formatPercent(counts.dismissalRate)
        )}
      </td>
    </>
  );
}

function RecommendationTable(props: {
  caption: string;
  kinds: readonly RecommendationKindFigures[];
}) {
  const { t } = useTranslation();
  const id = useId();
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const columns = [
    t("reports.recommendations.kind"),
    ...COUNT_COLUMNS.map((c) => t(`reports.recommendations.${c}`)),
    t("reports.recommendations.acceptance"),
    t("reports.recommendations.dismissal"),
  ];
  const modelName = (model: string | null) =>
    model === null
      ? t("reports.recommendations.unknownModel")
      : isModelKey(model)
        ? MODEL_CATALOGUE[model].displayName
        : model;
  const roleName = (role: string | null) =>
    role === "chat" || role === "reasoning" || role === "fast" || role === "vision"
      ? t(`reports.cost.roleLabel.${role}`)
      : t("reports.recommendations.unknownRole");
  // The first column stays in view while the figures scroll under it (opaque on purpose).
  const head = "sticky left-0 z-10 bg-surface-raised px-3 py-2 text-left";

  return (
    <div className="overflow-x-auto rounded-lg border border-line" style={SCROLL_CUE}>
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">{props.caption}</caption>
        <thead className="border-b border-line text-xs text-fg-muted">
          <tr>
            {columns.map((c, i) => (
              <th
                key={c}
                scope="col"
                className={cn(
                  "whitespace-nowrap px-3 py-2 font-medium",
                  i === 0 ? head : "text-right",
                )}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        {props.kinds.map((k, index) => {
          const expanded = open.has(k.kind);
          const details = `${id}-${k.kind}`;
          return (
            <Fragment key={k.kind}>
              <tbody className={cn(index > 0 && "border-t border-line")}>
                <tr>
                  <th scope="row" className={cn(head, "min-w-44 font-normal text-fg")}>
                    <button
                      type="button"
                      aria-expanded={expanded}
                      aria-controls={details}
                      onClick={() =>
                        setOpen((current) => {
                          const next = new Set(current);
                          if (!next.delete(k.kind)) {
                            next.add(k.kind);
                          }
                          return next;
                        })
                      }
                      className="-mx-1 inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-left font-medium hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                    >
                      <ChevronRight
                        aria-hidden="true"
                        strokeWidth={1.75}
                        className={cn(
                          "size-3.5 shrink-0 text-fg-muted transition-transform duration-(--duration-fast) ease-(--ease-out) motion-reduce:transition-none",
                          expanded && "rotate-90",
                        )}
                      />
                      {/* A kind this build does not know yet (a newer API) reads as its code. */}
                      {t(
                        `reports.recommendations.kinds.${k.kind}` as "reports.recommendations.kinds.memory_proposal",
                        {
                          defaultValue: k.kind,
                        },
                      )}
                    </button>
                  </th>
                  <CountCells counts={k} />
                </tr>
              </tbody>
              {/* Its model and prompt version rows, then the reasons people gave. */}
              <tbody id={details} hidden={!expanded} className="border-t border-line">
                {k.versions.map((v) => (
                  <tr key={`${v.modelRole}|${v.model}|${v.promptVersion}|${v.origin ?? ""}`}>
                    <th
                      scope="row"
                      className={cn(head, "pl-8 text-xs font-normal text-fg-secondary")}
                    >
                      {t("reports.recommendations.version", {
                        role: roleName(v.modelRole),
                        model: modelName(v.model),
                        version: v.promptVersion ?? t("reports.recommendations.unknownVersion"),
                      })}
                      {v.origin === "requested" || v.origin === "noticed"
                        ? ` · ${t(`reports.recommendations.origins.${v.origin}`)}`
                        : null}
                    </th>
                    <CountCells counts={v} muted />
                  </tr>
                ))}
                <tr>
                  <td colSpan={columns.length} className="px-3 py-2 pl-8 text-xs text-fg-secondary">
                    {k.reasons.length > 0
                      ? t("reports.recommendations.reasonsLine", {
                          reasons: k.reasons
                            .map(
                              (r) =>
                                `${t(`reports.recommendations.reasonLabel.${r.reason}`)} ${formatNumber(r.count)}`,
                            )
                            .join(" · "),
                        })
                      : t("reports.recommendations.noReasons")}
                  </td>
                </tr>
              </tbody>
            </Fragment>
          );
        })}
      </table>
    </div>
  );
}
